import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { api } from '../../../shared/services/API'
import { useNotification } from '../../../shared/contexts/NotificationContext'
import { getBackendError } from '../../../shared/utils/formErrors'
import type { Page } from '../../../shared/utils/types'
import { usePlatformAuth } from '../context/PlatformAuthContext'
import { PERM } from '../platformRoles'
import { ESTADO_TONE, PLANO_LABEL, formatDate, type TenantAdminDetail } from '../tenantModel'
import {
    CICLO_LABEL,
    ESTADO_SUBSCRICAO_LABEL,
    ESTADO_SUBSCRICAO_TONE,
    formatMoney,
    type Subscricao,
} from '../subscricoesModel'
import SubscricaoFormDialog from '../components/SubscricaoFormDialog'

function Field({ label, children }: { label: string; children: ReactNode }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25 }}>{children}</Typography>
        </Box>
    )
}

export default function TenantDetail() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { notify } = useNotification()
    const { hasPerm } = usePlatformAuth()
    const podeGerirTenant = hasPerm(PERM.TENANTS_MANAGE)
    const podeGerirSub = hasPerm(PERM.SUBSCRICOES_MANAGE)

    const [tenant, setTenant] = useState<TenantAdminDetail | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState('')
    const [acaoPendente, setAcaoPendente] = useState(false)

    // Subscrição corrente (a não-cancelada) da empresa — `null` = sem plano atribuído.
    const [sub, setSub] = useState<Subscricao | null>(null)
    const [subLoading, setSubLoading] = useState(true)
    const [subError, setSubError] = useState('')
    const [formOpen, setFormOpen] = useState(false)

    const carregarTenant = useCallback(() => {
        if (!id) return
        setIsLoading(true)
        setError('')
        api.get<TenantAdminDetail>(`/api/admin/tenants/${id}`)
            .then((r) => setTenant(r.data))
            .catch(() => setError('Não foi possível carregar os dados da empresa.'))
            .finally(() => setIsLoading(false))
    }, [id])

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- carregarTenant alterna a sua própria flag de loading
        carregarTenant()
    }, [carregarTenant])

    const carregarSub = useCallback(() => {
        if (!id) return
        setSubLoading(true)
        setSubError('')
        api.get<Page<Subscricao>>('/api/admin/subscricoes', {
            params: { tenantId: id, size: 10, sort: 'createdAt,desc' },
        })
            .then((r) => {
                const rows = r.data.content ?? []
                setSub(rows.find((s) => s.estado !== 'CANCELADA') ?? null)
            })
            .catch(() => setSubError('Não foi possível carregar a subscrição.'))
            .finally(() => setSubLoading(false))
    }, [id])

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- carregarSub alterna a sua própria flag de loading
        carregarSub()
    }, [carregarSub])

    // Estável depois de a empresa carregar — evita reiniciar o formulário do diálogo a cada render.
    const lockedTenant = useMemo(
        () => (tenant ? { id: tenant.id, nome: tenant.nome } : null),
        [tenant],
    )

    const ESTADO_ACCAO_LABEL: Record<'suspender' | 'reactivar' | 'cancelar', string> = {
        suspender: 'Suspender',
        reactivar: 'Reactivar',
        cancelar: 'Cancelar definitivamente',
    }

    const acaoTenant = async (accao: 'suspender' | 'reactivar' | 'cancelar') => {
        if (!id) return
        if (!window.confirm(`${ESTADO_ACCAO_LABEL[accao]} a empresa "${tenant?.nome ?? ''}"?`)) return
        setAcaoPendente(true)
        try {
            await api.patch(`/api/admin/tenants/${id}/${accao}`)
            notify('Empresa actualizada.')
            carregarTenant()
        } catch (err) {
            notify(getBackendError(err)?.message ?? 'Não foi possível concluir a acção.', 'error')
        } finally {
            setAcaoPendente(false)
        }
    }

    const cancelarSub = async () => {
        if (!sub) return
        if (!window.confirm(`Cancelar a subscrição de "${tenant?.nome ?? 'esta empresa'}"? A empresa deixa de ter acesso aos módulos funcionais.`)) return
        try {
            await api.post(`/api/admin/subscricoes/${sub.id}/cancelar`)
            notify('Subscrição cancelada.')
            carregarSub()
        } catch (err) {
            notify(getBackendError(err)?.message ?? 'Não foi possível cancelar a subscrição.', 'error')
        }
    }

    return (
        <Box>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/tenants')}
                sx={{ color: 'text.secondary', pl: 0, mb: 2 }}
            >
                Voltar às empresas
            </Button>

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
            )}

            {error && !isLoading && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

            {tenant && !isLoading && (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{tenant.nome}</Typography>
                        <Chip
                            size="small"
                            label={ESTADO_TONE[tenant.estado]?.label ?? tenant.estado}
                            sx={{
                                bgcolor: ESTADO_TONE[tenant.estado]?.bg ?? '#F3F4F6',
                                color: ESTADO_TONE[tenant.estado]?.color ?? '#374151',
                                fontWeight: 600,
                            }}
                        />
                        {podeGerirTenant && tenant.estado !== 'CANCELLED' && (
                            <Box sx={{ display: 'flex', gap: 1, ml: { sm: 'auto' } }}>
                                {tenant.estado === 'ACTIVE' && (
                                    <Button size="small" color="warning" variant="outlined" disabled={acaoPendente} onClick={() => acaoTenant('suspender')}>
                                        Suspender
                                    </Button>
                                )}
                                {tenant.estado === 'SUSPENDED' && (
                                    <Button size="small" color="success" variant="outlined" disabled={acaoPendente} onClick={() => acaoTenant('reactivar')}>
                                        Reactivar
                                    </Button>
                                )}
                                <Button size="small" color="error" disabled={acaoPendente} onClick={() => acaoTenant('cancelar')}>
                                    Cancelar empresa
                                </Button>
                            </Box>
                        )}
                    </Box>

                    <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3.5 }, border: '1px solid #E5E7EB', borderRadius: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Identificação</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                            <Field label="Email">{tenant.email || '—'}</Field>
                            <Field label="Telefone">{tenant.telefone || '—'}</Field>
                            <Field label="Schema">
                                <Box component="span" sx={{ fontFamily: 'monospace' }}>{tenant.schemaName}</Box>
                            </Field>
                            <Field label="Plano">{PLANO_LABEL[tenant.plano] ?? tenant.plano}</Field>
                            <Field label="Condomínios activos">{tenant.totalCondominios}</Field>
                            <Field label="Criado em">{formatDate(tenant.criadoEm)}</Field>
                        </Box>
                    </Paper>

                    <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3.5 }, border: '1px solid #E5E7EB', borderRadius: 3, mt: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Plano / Subscrição</Typography>
                            {podeGerirSub && !subLoading && !subError && (
                                sub ? (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button size="small" variant="outlined" onClick={() => setFormOpen(true)}>Alterar plano</Button>
                                        <Button size="small" color="error" onClick={cancelarSub}>Cancelar</Button>
                                    </Box>
                                ) : (
                                    <Button size="small" variant="contained" onClick={() => setFormOpen(true)}>Atribuir plano</Button>
                                )
                            )}
                        </Box>

                        {subLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>
                        ) : subError ? (
                            <Alert severity="error" sx={{ borderRadius: 2 }}>{subError}</Alert>
                        ) : sub ? (
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                                <Field label="Plano">{sub.planoNome ?? '—'}</Field>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        Estado
                                    </Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                        <Chip
                                            size="small"
                                            label={ESTADO_SUBSCRICAO_LABEL[sub.estado]}
                                            sx={{ fontWeight: 600, ...ESTADO_SUBSCRICAO_TONE[sub.estado] }}
                                        />
                                    </Box>
                                </Box>
                                <Field label="Ciclo de faturação">{CICLO_LABEL[sub.cicloFaturacao]}</Field>
                                <Field label="Preço">{formatMoney(sub.preco)}</Field>
                                <Field label="Início">{formatDate(sub.inicioEm)}</Field>
                                <Field label="Próxima faturação">{formatDate(sub.proximaFaturacaoEm)}</Field>
                            </Box>
                        ) : (
                            <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                Esta empresa não tem nenhum plano atribuído. Sem plano, os módulos funcionais ficam indisponíveis para os seus utilizadores.
                            </Alert>
                        )}
                    </Paper>

                    <SubscricaoFormDialog
                        open={formOpen}
                        initial={sub}
                        lockedTenant={sub ? null : lockedTenant}
                        onClose={() => setFormOpen(false)}
                        onSaved={() => {
                            setFormOpen(false)
                            notify(sub ? 'Subscrição actualizada.' : 'Plano atribuído à empresa.')
                            carregarSub()
                        }}
                    />
                </>
            )}
        </Box>
    )
}
